// @ts-nocheck
/* Starter Custom Code
import {
    CurrentLocationInfoWindow,
    WarehouseGoogleMapsMarker,
    WarehouseInfoWindow,
} from "@insite/client-framework/Common/Hooks/useWarehouseGoogleMarkers";
 */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import withDynamicGoogleMaps from "@insite/client-framework/Common/withDynamicGoogleMaps";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import WarehouseHoursLink from "@insite/content-library/Components/WarehouseHoursLink";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";

import { GoogleMapProps, InfoWindowProps, MarkerProps } from "@react-google-maps/api";
import React, { Fragment } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { locationProps } from "@store/Themes/common/props/location";
import useGoogleMapThemableMarkers from "@store/Framework/Common/Hooks/googleMap/useGoogleMapThemableMarkers";
import useGoogleMapThemableProps from "@store/Framework/Common/Hooks/googleMap/useGoogleMapThemableProps";
import useMergeProps from "@store/Framework/Common/Hooks/useMergeProps";
import {
    CurrentLocationInfoWindow,
    WarehouseGoogleMapsMarker,
    WarehouseInfoWindow,
} from "@store/Modules/client-framework/Common/Hooks/useWarehouseGoogleMarkers";
/* Starter Custom Code - End */

interface Props {
    extendedStyles?: WarehouseGoogleMapStyles;
    currentLocation?: google.maps.LatLng;
    warehouseSearchFilter: string;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    mapMarkerElements: WarehouseGoogleMapsMarker[];
    warehouseInfoWindow?: WarehouseInfoWindow;
    currentLocationInfoWindow?: CurrentLocationInfoWindow;
    overrideRenderWarehouseCard?: (warehouse: WarehouseModel) => React.ReactNode;
    handleOpenWarehouseHours: (warehouse: WarehouseModel) => void;
    setGoogleMap: (map: google.maps.Map | undefined) => void;
    updateLastFocusableElement: () => void;
    GoogleMap: React.ComponentType<GoogleMapProps>;
    InfoWindow: React.ComponentType<InfoWindowProps>;
    Marker: React.ComponentType<MarkerProps>;
}

export interface WarehouseGoogleMapStyles {
    googleMap?: GoogleMapProps;
    warehouseMarker?: MarkerProps;
    currentLocationMarker?: MarkerProps;
    selectedMarker?: MarkerProps;
    warehouseInfoWindow?: InfoWindowProps;
    currentLocationInfoWindow?: InfoWindowProps;
    infoWindowGridContainer?: GridContainerProps;
    infoWindowLeftGridItem?: GridItemProps;
    selectedLocationName?: TypographyPresentationProps;
    selectedLocationAddressDisplay?: AddressInfoCondensedDisplayStyles;
    infoWindowRightGridItem?: GridItemProps;
    infoWindowWarehouseHoursLink?: LinkPresentationProps;
    infoWindowGoogleMapsDirectionLink?: LinkPresentationProps;
    infoWindowDistanceDisplay?: TypographyPresentationProps;
    currentLocationInfoWindowGridContainer?: GridContainerProps;
    currentLocationInfoWindowHeaderGridItem?: GridItemProps;
    currentLocationInfoWindowHeaderText?: TypographyPresentationProps;
    currentLocationInfoWindowSearchFilterGridItem?: GridItemProps;
    currentLocationInfoWindowSearchFilterText?: TypographyPresentationProps;
    // Starter Custom Code
    infoWindowWrapper?: InjectableCss;
    infoWindowTitleWrapper?: InjectableCss;
}

export const warehouseGoogleMapStyles: WarehouseGoogleMapStyles = {
    infoWindowWrapper: locationProps.infoWindow?.wrapper,
    infoWindowTitleWrapper: locationProps.infoWindow?.titleWrapper,
    infoWindowDistanceDisplay: locationProps.infoWindow?.distanceText,
    selectedLocationName: locationProps.infoWindow?.title,
    selectedLocationAddressDisplay: locationProps.infoWindow?.addressInfo,
    currentLocationInfoWindowHeaderText: locationProps.infoWindow?.title,
    infoWindowWarehouseHoursLink: locationProps.infoWindow?.actionLink,
    infoWindowGoogleMapsDirectionLink: locationProps.infoWindow?.actionLink,
};

const WarehouseGoogleMap: React.FC<Props> = ({
    extendedStyles,
    currentLocation,
    warehouseSearchFilter,
    distanceUnitOfMeasure,
    setGoogleMap,
    mapMarkerElements: baseMapMarkerElements, // Starter Custom Code
    warehouseInfoWindow,
    currentLocationInfoWindow,
    overrideRenderWarehouseCard,
    handleOpenWarehouseHours,
    GoogleMap,
    InfoWindow,
    Marker,
}) => {
    /* Starter Custom Code - Start */
    const styles = useMergeProps(warehouseGoogleMapStyles, extendedStyles);
    const googleMapProps = useGoogleMapThemableProps(styles.googleMap);
    const mapMarkerElements = useGoogleMapThemableMarkers(baseMapMarkerElements);
    /* Starter Custom Code - End */

    if (!currentLocation) {
        return null;
    }

    const handleLoad = (map: google.maps.Map) => {
        setGoogleMap(map);
    };
    const handleUnmount = (_: google.maps.Map) => {
        setGoogleMap(undefined);
    };

    return (
        <GoogleMap
            id="GoogleMap"
            {...googleMapProps} // Starter Custom Code
            center={currentLocation}
            onLoad={handleLoad}
            onUnmount={handleUnmount}
        >
            {mapMarkerElements.map(marker => (
                <Fragment key={marker.key}>
                    {marker.type === "WAREHOUSE" && <Marker {...marker} key={marker.key} {...styles.warehouseMarker} />}
                    {marker.type === "CURRENT_LOCATION" && (
                        <Marker {...marker} key={marker.key} {...styles.currentLocationMarker} />
                    )}
                    {marker.type === "SELECTED" && <Marker {...marker} key={marker.key} {...styles.selectedMarker} />}
                </Fragment>
            ))}
            {warehouseInfoWindow && (
                <InfoWindow {...styles.warehouseInfoWindow} key={newGuid()} position={warehouseInfoWindow?.position}>
                    {/* Starter Custom Code - Start */}
                    {overrideRenderWarehouseCard ? (
                        overrideRenderWarehouseCard(warehouseInfoWindow.warehouse)
                    ) : (
                        <StyledWrapper {...styles.infoWindowWrapper}>
                            <StyledWrapper {...styles.infoWindowTitleWrapper}>
                                <Typography {...styles.selectedLocationName}>
                                    {warehouseInfoWindow.warehouse.description || warehouseInfoWindow.warehouse.name}
                                </Typography>
                                {warehouseInfoWindow.warehouse.distance > 0.01 && (
                                    <DistanceDisplay
                                        {...styles.infoWindowDistanceDisplay}
                                        distance={warehouseInfoWindow.warehouse.distance}
                                        unitOfMeasure={distanceUnitOfMeasure}
                                    />
                                )}
                            </StyledWrapper>
                            <AddressInfoCondensedDisplay
                                {...warehouseInfoWindow.warehouse}
                                extendedStyles={styles.selectedLocationAddressDisplay}
                            />
                            {warehouseInfoWindow.warehouse.hours && (
                                <WarehouseHoursLink
                                    {...styles.infoWindowWarehouseHoursLink}
                                    warehouse={warehouseInfoWindow.warehouse}
                                    onOpenWarehouseHours={handleOpenWarehouseHours}
                                />
                            )}
                            <GoogleMapsDirectionLink
                                {...styles.infoWindowGoogleMapsDirectionLink}
                                {...warehouseInfoWindow.warehouse}
                            />
                        </StyledWrapper>
                    )}
                    {/* Starter Custom Code - End */}
                </InfoWindow>
            )}
            {currentLocationInfoWindow && (
                <InfoWindow
                    {...styles.currentLocationInfoWindow}
                    key={newGuid()}
                    position={currentLocationInfoWindow.position}
                >
                    {/* Starter Custom Code - Start */}
                    <StyledWrapper {...styles.infoWindowWrapper}>
                        <StyledWrapper {...styles.infoWindowTitleWrapper}>
                            <Typography {...styles.currentLocationInfoWindowHeaderText}>
                                {translate("Current Location")}
                            </Typography>
                        </StyledWrapper>
                        <Typography {...styles.currentLocationInfoWindowSearchFilterText}>
                            {warehouseSearchFilter}
                        </Typography>
                    </StyledWrapper>
                    {/* Starter Custom Code - End */}
                </InfoWindow>
            )}
        </GoogleMap>
    );
};

export default withDynamicGoogleMaps(WarehouseGoogleMap);
